/**
 * GoogleMapPicker - A reusable Google Maps location picker component
 * Uses iframe embed for compatibility + Geocoding API for search
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { MapPin, Search, Loader2, X, Navigation } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const GoogleMapPicker = ({
  latitude,
  longitude,
  address,
  onLocationChange,
  height = '300px',
  showSearch = true,
  showCoordinates = true,
  placeholder = 'Search for a location...'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(address || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({
    lat: latitude || null,
    lng: longitude || null,
    address: address || ''
  });
  const searchTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Geocode search query
  const searchLocation = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(query)}&key=${GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        setSuggestions(data.results.slice(0, 5).map(result => ({
          address: result.formatted_address,
          lat: result.geometry.location.lat,
          lng: result.geometry.location.lng,
          components: result.address_components
        })));
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(value);
    }, 300);
  };

  // Extract address components
  const extractAddressComponents = (components) => {
    if (!components) return {};
    
    const result = {};
    const typeMap = {
      'locality': 'city',
      'administrative_area_level_1': 'region',
      'administrative_area_level_2': 'district',
      'country': 'country',
      'postal_code': 'postal_code',
      'neighborhood': 'neighborhood',
      'sublocality': 'neighborhood'
    };

    components.forEach(component => {
      component.types.forEach(type => {
        if (typeMap[type]) {
          result[typeMap[type]] = component.long_name;
          if (type === 'country') {
            result.country_code = component.short_name;
          }
        }
      });
    });

    return result;
  };

  // Select a suggestion
  const selectSuggestion = (suggestion) => {
    const newLocation = {
      lat: suggestion.lat,
      lng: suggestion.lng,
      address: suggestion.address,
      ...extractAddressComponents(suggestion.components)
    };
    
    setCurrentLocation(newLocation);
    setSearchValue(suggestion.address);
    setShowSuggestions(false);
    setSuggestions([]);
    
    if (onLocationChange) {
      onLocationChange(newLocation);
    }
  };

  // Use current device location
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        
        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
          );
          const data = await response.json();
          
          let addressText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          let addressComponents = {};
          
          if (data.results && data.results[0]) {
            addressText = data.results[0].formatted_address;
            addressComponents = extractAddressComponents(data.results[0].address_components);
          }
          
          const newLocation = {
            lat,
            lng,
            address: addressText,
            ...addressComponents
          };
          
          setCurrentLocation(newLocation);
          setSearchValue(addressText);
          
          if (onLocationChange) {
            onLocationChange(newLocation);
          }
        } catch (error) {
          console.error('Reverse geocoding error:', error);
          const newLocation = { lat, lng, address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` };
          setCurrentLocation(newLocation);
          if (onLocationChange) {
            onLocationChange(newLocation);
          }
        }
        
        setIsLoading(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location');
        setIsLoading(false);
      }
    );
  };

  // Clear location
  const clearLocation = () => {
    setSearchValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    setCurrentLocation({ lat: null, lng: null, address: '' });
    
    if (onLocationChange) {
      onLocationChange({ lat: null, lng: null, address: '' });
    }
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update state when props change
  useEffect(() => {
    if (latitude && longitude) {
      setCurrentLocation(prev => ({
        ...prev,
        lat: latitude,
        lng: longitude,
        address: address || prev.address
      }));
    }
  }, [latitude, longitude, address]);

  // Generate map embed URL
  const getMapUrl = () => {
    if (currentLocation.lat && currentLocation.lng) {
      return `https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${currentLocation.lat},${currentLocation.lng}&zoom=15`;
    }
    return `https://www.google.com/maps/embed/v1/view?key=${GOOGLE_MAPS_API_KEY}&center=0,0&zoom=2`;
  };

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center text-slate-500">
          <MapPin className="h-8 w-8 mx-auto mb-2 text-slate-400" />
          <p>Google Maps API key not configured</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" ref={containerRef}>
      {showSearch && (
        <div className="relative">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
              <input
                type="text"
                placeholder={placeholder}
                value={searchValue}
                onChange={handleSearchChange}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 pl-10 pr-10"
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={clearLocation}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 z-10"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={useCurrentLocation}
              disabled={isLoading}
              title="Use my current location"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Navigation className="h-4 w-4" />
              )}
            </Button>
          </div>
          
          {/* Search suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-start gap-2"
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <MapPin className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                  <span className="line-clamp-2">{suggestion.address}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map embed - using iframe to avoid React DOM conflicts */}
      <div
        className="rounded-lg overflow-hidden border bg-slate-100"
        style={{ height, minHeight: '200px' }}
      >
        <iframe
          src={getMapUrl()}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Location Map"
        />
      </div>

      {showCoordinates && currentLocation.lat && currentLocation.lng && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-xs text-slate-500">Latitude</Label>
            <p className="font-mono text-slate-700">{currentLocation.lat?.toFixed(6)}</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Longitude</Label>
            <p className="font-mono text-slate-700">{currentLocation.lng?.toFixed(6)}</p>
          </div>
        </div>
      )}

      {currentLocation.address && (
        <div className="text-sm">
          <Label className="text-xs text-slate-500">Selected Address</Label>
          <p className="text-slate-700">{currentLocation.address}</p>
        </div>
      )}
    </div>
  );
};

export default GoogleMapPicker;
